import { useSelector } from 'react-redux'
import { usePapeletas } from './hooks/use-papeletas'
import { useMemo, useState } from 'react'
import { verifyRoleAccess } from 'helpers/verify-role-access'
import { Button, Modal } from 'react-bootstrap'
import {
    Ban,
    Clock,
    CloudUpload,
    Eye,
    FileText,
    FunnelX,
    List,
    Plus,
    Printer,
    RefreshCcw,
} from 'lucide-react'
import { useTable } from 'hooks/use-table'
import { getCoreRowModel } from '@tanstack/react-table'
import { PapeletaService } from './services/papeleta.service'
import { showHttpToast } from 'lib/show-toast'
import { Loader } from 'components/molecules/loader'
import { AppContentWrapper } from 'components/app/app-content-wrapper'
import { PaginationControls } from 'components/molecules/pagination-controls'
import { Confirm } from 'components/molecules/confirm'
import { DataTable } from 'components/molecules/data-table'
import { Select } from 'components/molecules/select'
import { papeletaStatusLabels, papeletaStatusOptions } from './data/papeleta.constants'
import { dateCellTemplate } from 'components/templates/date-cell-template'
import { PapeletaViewModal } from './components/PapeletaViewModal'
import PapeletaPdf from 'pdf/papeleta'
import printJS from 'print-js'
import { PapeletaRefundForm } from './components/PapeletaRefundForm'
import { Link } from 'react-router-dom'
import { PapeletaUploadForm } from './components/PapeletaUploadForm'
import { PapeletaViewPdfModal } from './components/PapeletaViewPdfModal'
import { dateFilterElement } from 'components/templates/date-filter-element'
import { openBlob } from 'utils/open-blob'

const papeletaStatusStyles = {
    N: { background: '#DFF2FE', color: '#00598A', border: '1px solid #B8E6FE' },
    P: { background: '#DBEAFE', color: '#193CBB', border: '1px solid #BEDBFF' },
    E: { background: '#F3F4F6', color: '#1E2939', border: '1px solid #E5E7EB' },
    T: { background: '#DCFCE7', color: '#016630', border: '1px solid #B9F8CF' },
    A: { background: '#FFE2E2', color: '#9F0712', border: '1px solid #FFC9C9' },
}
const pageSizeOptions = [10, 25, 50]
const baseColumns = [
    {
        accessorKey: 'numero',
        header: 'Nro. papeleta',
        className: 'text-center',
        style: { width: '10rem' },
    },
    { accessorKey: 'doc_autorizacion', header: 'Doc. autorización', style: { width: '12rem' } },
    { accessorKey: 'beneficiario', header: 'Beneficiario' },
    { accessorKey: 'lugar_viaje', header: 'Lugar de viaje' },
    {
        accessorKey: 'estado',
        header: 'Estado',
        cell: ({ row }) => {
            const value = row.original.estado
            return (
                <span className='badge rounded-pill fw-medium' style={papeletaStatusStyles[value]}>
                    {papeletaStatusLabels[value] ?? 'Indeterminado'}
                </span>
            )
        },
        filterElement: ({ value, filterCallback }) => (
            <Select
                options={papeletaStatusOptions}
                value={value}
                onChange={(e) => filterCallback(e.target.value)}
                placeholder='Todos'
            />
        ),
        className: 'text-center',
        style: { width: '10rem' },
    },
    {
        accessorKey: 'fecha_creacion',
        header: 'Fecha',
        cell: dateCellTemplate,
        filterElement: dateFilterElement,
        style: { width: '10rem' },
    },
]
export function PapeletaListPage() {
    const user = useSelector((state) => state.auth)
    const { sectionKey } = useSelector((state) => state.system)
    const {
        data: papeletas,
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
        pagination,
        handlePageChange,
        handlePageSizeChange,
        reloadQueryParams,
        resetQueryParams,
        initializing,
    } = usePapeletas()
    const [isVisible, setIsVisible] = useState({
        refundFormModal: false,
        confirmCancelModal: false,
        viewModal: false,
        uploadFormModal: false,
        downloadModal: false,
    })
    const [selected, setSelected] = useState(null)

    const show = (name) => setIsVisible((prev) => ({ ...prev, [name]: true }))
    const hide = (name) => setIsVisible((prev) => ({ ...prev, [name]: false }))

    const handleAction = (action, data = null) => {
        setSelected(data)
        if (action === 'cancel') {
            show('confirmCancelModal')
        } else if (action === 'view') {
            show('viewModal')
        } else if (action === 'refund') {
            show('refundFormModal')
        } else if (action === 'upload_pdf') {
            show('uploadFormModal')
        } else if (action === 'download_pdf') {
            show('downloadModal')
        }
    }
    const columns = useMemo(() => {
        const canView = verifyRoleAccess(user, 'papeleta:view')
        const canCancel = verifyRoleAccess(user, 'papeleta:cancel')
        const canGeneratePdf = verifyRoleAccess(user, 'papeleta:generate_pdf')
        const canUploadPdf = verifyRoleAccess(user, 'papeleta:upload_pdf')
        const canDownloadPdf = verifyRoleAccess(user, 'papeleta:download_pdf')
        const canRefund = verifyRoleAccess(user, 'papeleta:refund')
        if (
            !canView &&
            !canCancel &&
            !canGeneratePdf &&
            !canUploadPdf &&
            !canRefund &&
            !canDownloadPdf
        ) {
            return baseColumns
        }
        return [
            ...baseColumns,
            {
                id: 'options',
                header: 'Opciones',
                cell: ({ row }) => (
                    <div className='space-x-2 text-center text-nowrap'>
                        {canView && (
                            <Button
                                variant='link'
                                size='icon-sm'
                                title='Detalle'
                                onClick={() => handleAction('view', row.original)}
                            >
                                <Eye />
                            </Button>
                        )}
                        {canGeneratePdf && (
                            <Button
                                variant='link'
                                size='icon-sm'
                                title='Imprimir'
                                onClick={() => handleGeneratePdf(row.original.id)}
                                disabled={row.original.estado === 'A'}
                            >
                                <Printer />
                            </Button>
                        )}
                        {canUploadPdf && !row.original.adjunto && (
                            <Button
                                variant='link'
                                size='icon-sm'
                                title='Adjuntar papeleta'
                                onClick={() => handleAction('upload_pdf', row.original)}
                            >
                                <CloudUpload />
                            </Button>
                        )}
                        {canDownloadPdf && row.original.adjunto && (
                            <Button
                                variant='link'
                                size='icon-sm'
                                title='Ver PDF'
                                onClick={() => handleAction('download_pdf', row.original)}
                            >
                                <FileText />
                            </Button>
                        )}
                        {canCancel && (
                            <Button
                                variant='link'
                                size='icon-sm'
                                title='Anular'
                                className={
                                    ['N', 'P'].includes(row.original.estado)
                                        ? 'text-danger'
                                        : undefined
                                }
                                onClick={() => handleAction('cancel', row.original)}
                                disabled={!['N', 'P'].includes(row.original.estado)}
                            >
                                <Ban />
                            </Button>
                        )}
                        {canRefund && (
                            <Button
                                variant='link'
                                size='icon-sm'
                                title='Devolución'
                                onClick={() => handleAction('refund', row.original)}
                                disabled={row.original.estado !== 'P'}
                            >
                                <RefreshCcw />
                            </Button>
                        )}
                    </div>
                ),
                filterElement: () => (
                    <Button variant='blue' onClick={() => resetQueryParams()}>
                        <FunnelX className='size-4' />
                        Limpiar
                    </Button>
                ),
                enableColumnFilter: true,
            },
        ]
    }, [user])
    const table = useTable({
        data: papeletas,
        columns,
        state: { sorting, columnFilters },
        enableSorting: true,
        enableColumnFilters: true,
        manualSorting: true,
        manualFiltering: true,
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
    })

    const handleGeneratePdf = async (id) => {
        if (user.userInfo.fuente_financiamiento === 'DAF') {
            const { success, data } = await PapeletaService.getPdfData(id)
            if (success) {
                const pdfP = new PapeletaPdf()
                pdfP.papeleta(data.data, data.configPapeleta)
            }
        } else {
            const { success, data } = await PapeletaService.generatePdf(id)
            if (success) {
                const reader = new FileReader()
                reader.onloadend = () => {
                    const base64 = reader.result.split(',')[1]
                    printJS({
                        printable: base64,
                        type: 'pdf',
                        base64: true,
                    })
                }
                reader.readAsDataURL(data)
            }
        }
    }
    const handleCancel = async () => {
        hide('confirmCancelModal')
        if (selected) {
            const { success, message } = await PapeletaService.cancel(selected.id)
            showHttpToast(success, { description: message })
            if (success) reloadQueryParams()
        }
    }
    const generatePendientesPdf = async () => {
        const { success, data } = await PapeletaService.generatePendientesPdf()
        if (success) {
            openBlob(data)
        } else {
            showHttpToast(success, { description: 'Ocurrió un error al obtener el PDF' })
        }
    }

    if (initializing) return <Loader />

    const rightHeader = (
        <div>
            {sectionKey === 'caja' && (
                <Button onClick={generatePendientesPdf}>
                    <Clock className='size-4' />
                    Pendientes PDF
                </Button>
            )}
            {verifyRoleAccess(user, ['papeleta:pay', 'papeleta:reverse'], false) && (
                <Button variant='light' as={Link} to='/caja/viaticos/accion'>
                    <List className='size-4' />
                    Pagar/Revertir
                </Button>
            )}
            {verifyRoleAccess(user, 'papeleta:create') && (
                <Button variant='light' as={Link} to='/viaticos/papeleta/create'>
                    <Plus className='size-4' />
                    Nuevo
                </Button>
            )}
        </div>
    )

    return (
        <AppContentWrapper header='Lista de papeletas de viáticos' rightHeader={rightHeader}>
            <DataTable
                table={table}
                className='table-striped table-hover'
                defaultAlignHeader='center'
            />
            <div className='flex justify-end'>
                <PaginationControls
                    totalCount={pagination.totalRecords}
                    page={pagination.currentPage}
                    pageSize={pagination.pageSize}
                    onPageChange={handlePageChange}
                    visiblePages={5}
                    pageSizeOptions={pageSizeOptions}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>
            <Modal
                show={isVisible.refundFormModal}
                onHide={() => hide('refundFormModal')}
                dialogClassName='w-120'
                centered
            >
                <Modal.Header className='py-2'>
                    <Modal.Title>Devolución</Modal.Title>
                </Modal.Header>
                <PapeletaRefundForm
                    papeleta={selected}
                    onCancel={() => hide('refundFormModal')}
                    onSuccess={() => {
                        reloadQueryParams()
                        hide('refundFormModal')
                    }}
                />
            </Modal>
            <Modal
                show={isVisible.uploadFormModal}
                onHide={() => hide('uploadFormModal')}
                dialogClassName='w-25rem'
                centered
            >
                <Modal.Header className='py-2'>
                    <Modal.Title>Subir PDF</Modal.Title>
                </Modal.Header>
                <PapeletaUploadForm
                    id={selected?.id}
                    onCancel={() => hide('uploadFormModal')}
                    onSuccess={() => {
                        reloadQueryParams()
                        hide('uploadFormModal')
                    }}
                />
            </Modal>
            <PapeletaViewPdfModal
                show={isVisible.downloadModal}
                onClose={() => hide('downloadModal')}
                id={selected?.id}
            />
            <PapeletaViewModal
                show={isVisible.viewModal}
                onClose={() => hide('viewModal')}
                id={selected?.id}
            />
            <Confirm
                show={isVisible.confirmCancelModal}
                onAccept={handleCancel}
                onReject={() => hide('confirmCancelModal')}
            >
                ¿Esta seguro de <span className='text-primary'>ANULAR</span> la papeleta &quot;
                {selected?.numero}&quot;?
            </Confirm>
        </AppContentWrapper>
    )
}
